import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Pencil } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const dateOnly = (s) => (s ? String(s).slice(0, 10) : "");

export default function PSAPossessionEditDialog({ row, persons, onClose, onSaved }) {
  const [givenById, setGivenById] = useState("");
  const [receivedDate, setReceivedDate] = useState(dateOnly(row?.receivedAt));
  const [returnedDate, setReturnedDate] = useState(dateOnly(row?.returnedAt));
  const [takenById, setTakenById] = useState("");
  const [returnedTo, setReturnedTo] = useState(row?.returnedTo || "");
  const [busy, setBusy] = useState(false);

  if (!row) return null;
  const sorted = [...persons].sort((a, b) => `${a.nume || ""} ${a.prenume || ""}`.localeCompare(`${b.nume || ""} ${b.prenume || ""}`));
  const personName = (pid) => {
    const p = persons.find((x) => x.id === pid);
    return p ? `${p.nume || ""} ${p.prenume || ""}`.trim() : "";
  };

  const save = async () => {
    setBusy(true);
    try {
      const ops = [];
      // Primire
      const recvPatch = {};
      if (receivedDate && receivedDate !== dateOnly(row.receivedAt)) recvPatch.ts = receivedDate;
      if (givenById) { recvPatch.picked_up_by = personName(givenById); recvPatch.picked_up_by_id = givenById; }
      if (row.receiveEventId && Object.keys(recvPatch).length > 0) {
        ops.push(api.put(`/inventory/history/${row.receiveEventId}`, recvPatch));
      }
      if (row.inPossession && (recvPatch.ts || givenById)) {
        const itemPatch = {};
        if (recvPatch.ts) itemPatch.assigned_at = recvPatch.ts;
        if (givenById) { itemPatch.assigned_by = personName(givenById); itemPatch.assigned_by_id = givenById; }
        ops.push(api.patch(`/inventory/${row.itemId}/assignment-meta`, itemPatch));
      }
      // Predare
      if (row.closeEventId) {
        const closePatch = {};
        if (returnedDate && returnedDate !== dateOnly(row.returnedAt)) closePatch.ts = returnedDate;
        if (takenById) { closePatch.picked_up_by = personName(takenById); closePatch.picked_up_by_id = takenById; }
        if (row.closeToField && returnedTo && returnedTo !== row.returnedTo) closePatch[row.closeToField] = returnedTo;
        if (Object.keys(closePatch).length > 0) ops.push(api.put(`/inventory/history/${row.closeEventId}`, closePatch));
      }
      if (ops.length === 0) { toast.info("Nicio modificare de salvat"); setBusy(false); return; }
      await Promise.all(ops);
      toast.success("✓ Modificările au fost salvate");
      onSaved();
    } catch (e) {
      console.error("possession edit failed", e);
      toast.error("Eroare la salvare");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="psa-possession-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil size={16} className="text-cyan-700"/> Editează — {row.itemName}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold">📅 Data primirii</Label>
              <Input type="date" value={receivedDate} onChange={(e) => setReceivedDate(e.target.value)} data-testid="possession-edit-received-date"/>
            </div>
            <div>
              <Label className="text-xs font-bold">👤 Cine i-a dat</Label>
              <Select value={givenById || "__keep__"} onValueChange={(v) => setGivenById(v === "__keep__" ? "" : v)}>
                <SelectTrigger className="h-9 text-sm" data-testid="possession-edit-given-by">
                  <SelectValue placeholder={row.givenBy || "—"}/>
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="__keep__">— Păstrează: {row.givenBy || "—"} —</SelectItem>
                  {sorted.map((p) => (
                    <SelectItem key={`gb-${p.id}`} value={p.id}>{p.nume} {p.prenume}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {row.closeEventId ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-bold">📅 Data predării</Label>
                  <Input type="date" value={returnedDate} onChange={(e) => setReturnedDate(e.target.value)} data-testid="possession-edit-returned-date"/>
                </div>
                <div>
                  <Label className="text-xs font-bold">👤 Preluat de</Label>
                  <Select value={takenById || "__keep__"} onValueChange={(v) => setTakenById(v === "__keep__" ? "" : v)}>
                    <SelectTrigger className="h-9 text-sm" data-testid="possession-edit-taken-by">
                      <SelectValue placeholder={row.takenBy || "—"}/>
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="__keep__">— Păstrează: {row.takenBy || "—"} —</SelectItem>
                      {sorted.map((p) => (
                        <SelectItem key={`tb-${p.id}`} value={p.id}>{p.nume} {p.prenume}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {row.closeToField && (
                <div>
                  <Label className="text-xs font-bold">📍 Predat către</Label>
                  <Input value={returnedTo} onChange={(e) => setReturnedTo(e.target.value)} data-testid="possession-edit-returned-to"/>
                </div>
              )}
            </>
          ) : (
            <div className="text-[11px] text-zinc-500 italic bg-zinc-50 border border-zinc-200 rounded px-2 py-1.5">
              Articolul este încă în posesia angajatului — datele de predare se completează la preluare.
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Anulează</Button>
          <Button onClick={save} disabled={busy} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="possession-edit-save">
            {busy ? "Se salvează..." : "💾 Salvează"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
